import { useEffect, useState } from 'react';
import {
    getExpiringImplants,
    extendImplantExpiry,
} from '../../services/apiService';
import ImplantVerificationDisplay from './ImplantVerificationDisplay';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

const ImplantVerification = () => {
    // States for data, loading, and error handling
    const [implants, setImplants] = useState([]);
    const [activeImplants, setActiveImplants] = useState([]);
    const [passiveImplants, setPassiveImplants] = useState([]);
    const [implantToList, setImplantToList] = useState([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // States for implant selection and bulk operations
    const [selectedImplants, setSelectedImplants] = useState<string[]>([]);
    const [extendingExpiry, setExtendingExpiry] = useState<boolean>(false);

    // Fetching expiring implants
    const fetchImplantsData = async () => {
        try {
            const implantsData = await getExpiringImplants();
            const allImplants = implantsData.implants;

            // Split implants by category
            const active = allImplants.filter(
                (implant: any) => implant.Category === 'Active'
            );
            const passive = allImplants.filter(
                (implant: any) =>
                    implant.Category === 'Passive' ||
                    implant.Category === 'Stent'
            );

            setImplants(allImplants);
            setActiveImplants(active);
            setPassiveImplants(passive);
            setImplantToList(allImplants.slice(0, 100));
            setLoading(false);
            setError(null);
        } catch (error) {
            setLoading(false);
            setError(
                'There was an error fetching the data. Please try again later.'
            );
        }
    };

    // Handle individual checkbox changes
    const handleImplantSelection = (implantId: string, checked: boolean) => {
        setSelectedImplants((prev) => {
            if (checked) {
                return [...prev, implantId];
            } else {
                return prev.filter((id) => id !== implantId);
            }
        });
    };

    // Handle bulk extend expiry
    const handleBulkExtendExpiry = async () => {
        if (selectedImplants.length === 0) {
            alert('Please select at least one implant to extend expiry dates.');
            return;
        }

        setExtendingExpiry(true);
        try {
            // Extend expiry for each selected implant
            const promises = selectedImplants.map((implantId) =>
                extendImplantExpiry(implantId)
            );

            await Promise.all(promises);

            // Clear selections and refresh data
            setSelectedImplants([]);
            await fetchImplantsData();
        } catch (error) {
            setError('Failed to extend expiry dates. Please try again.');
        } finally {
            setExtendingExpiry(false);
        }
    };

    useEffect(() => {
        fetchImplantsData();
    }, []);

    return (
        <div className="container mx-auto max-w-7xl px-4 py-8">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold">Implant Verification</h1>
                    <p className="text-sm text-muted-foreground">
                        Review and verify implants based on view frequency and expiring dates.
                    </p>
                </div>
            </div>

            <Tabs defaultValue="active" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="active">
                        Active Implants ({activeImplants.length})
                    </TabsTrigger>
                    <TabsTrigger value="passive">
                        Passive Implants ({passiveImplants.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="mt-0">
                    <ImplantVerificationDisplay
                        implants={activeImplants}
                        loading={loading}
                        error={error}
                        selectedImplants={selectedImplants}
                        extendingExpiry={extendingExpiry}
                        onImplantSelection={handleImplantSelection}
                        onBulkExtendExpiry={handleBulkExtendExpiry}
                        onRefreshData={fetchImplantsData}
                    />
                </TabsContent>

                <TabsContent value="passive" className="mt-0">
                    <ImplantVerificationDisplay
                        implants={passiveImplants}
                        loading={loading}
                        error={error}
                        selectedImplants={selectedImplants}
                        extendingExpiry={extendingExpiry}
                        onImplantSelection={handleImplantSelection}
                        onBulkExtendExpiry={handleBulkExtendExpiry}
                        onRefreshData={fetchImplantsData}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ImplantVerification;
